import { useState, useEffect } from 'react';
import { h5GamesAds } from '../services/h5GamesAds';

interface AdInterstitialProps {
  isOpen: boolean;
  onComplete: () => void;
  adType?: 'next' | 'pause' | 'browse';
  placementName?: string;
}

type AdState = 'ready' | 'showing' | 'complete' | 'error' | 'skipped';

/**
 * H5 Games Ads 전면 광고 컴포넌트
 * 
 * Google Ad Placement API를 사용하여:
 * - 자연스러운 타이밍에 광고 표시
 * - 광고 없으면 자동으로 다음 단계 진행
 * - 게임 흐름을 방해하지 않음
 */
export default function AdInterstitial({ 
  isOpen, 
  onComplete,
  adType = 'next',
  placementName = 'game-end'
}: AdInterstitialProps) {
  const [adState, setAdState] = useState<AdState>('ready');
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAdState('ready');
      setShowFallback(false);
      return;
    }

    // H5 Games Ads 초기화
    h5GamesAds.initialize();

    // 광고 표시 시도
    setAdState('showing');
    
    // 잠시 후 fallback UI 표시 (광고 로딩 중 표시)
    const fallbackTimer = setTimeout(() => {
      setShowFallback(true);
    }, 500);

    h5GamesAds.showInterstitial(adType, placementName, {
      onStart: () => {
        console.log('[AdInterstitial] Ad started');
        setShowFallback(false); // 실제 광고가 시작되면 fallback 숨김
      },
      onComplete: () => {
        console.log('[AdInterstitial] Ad completed');
        setAdState('complete');
      },
      onError: (status) => {
        console.log('[AdInterstitial] Ad error/skipped:', status);
        // 광고가 없거나 에러여도 게임 진행
        setAdState('skipped');
      }
    }).then((shown) => {
      clearTimeout(fallbackTimer);
      
      if (!shown) {
        // 광고가 표시되지 않았으면 바로 완료 처리
        console.log('[AdInterstitial] No ad shown, proceeding');
        setAdState('skipped');
      }
      
      // 약간의 딜레이 후 완료 콜백
      setTimeout(() => {
        onComplete();
      }, shown ? 500 : 100);
    });

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [isOpen, adType, placementName, onComplete]);

  // 열려있지 않으면 렌더링 안함
  if (!isOpen) return null;

  // 광고가 실제로 표시되는 동안은 H5 API가 자체 UI를 보여줌
  // 여기서는 로딩/전환 UI만 표시

  // 완료 또는 스킵 상태면 아무것도 표시 안함
  if (adState === 'complete' || adState === 'skipped') {
    return null;
  }

  // Fallback UI (광고 로딩 중 또는 광고 없을 때)
  if (showFallback) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-3 border-primary-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-400 text-sm">잠시만 기다려주세요...</p>
        </div>
      </div>
    );
  }

  return null;
}
